import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslateModule } from '@ngx-translate/core';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { ButtonComponent } from 'ui-library';

export interface TemplatePickerDialogData {
  typeFilter?: TrackerConfig['type'];
  // Set when picking a template for a specific device in a sequential ("un par un") bulk-apply
  // flow (resources.component.ts's applyTemplateSequentially()) - shown in the header so the
  // user knows which device this choice is for, since several are picked back-to-back in the
  // same dialog instance.
  trackerName?: string;
}

// Shared "pick one existing TrackerConfig template, nothing else" dialog - the bulk-apply
// counterpart of tracker-config-modal.page.ts's own picker on mobile. Deliberately separate
// from kyd-bulk-config-dialog.component.ts (which lets a template prefill an EDITABLE form
// before applying) - this one applies the template's values as-is, immediately, to whichever
// selection the caller passes it.
@Component({
  selector: 'app-template-picker-dialog',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatDialogModule,
    MatIconModule,
    MatToolbar,
    ButtonComponent,
  ],
  standalone: true,
  templateUrl: './template-picker-dialog.component.html',
  styleUrls: ['./template-picker-dialog.component.scss']
})
export class TemplatePickerDialogComponent implements OnInit {
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly data: TemplatePickerDialogData = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<TemplatePickerDialogComponent, TrackerConfig | undefined> =
    inject(MatDialogRef<TemplatePickerDialogComponent, TrackerConfig | undefined>);

  readonly trackerName?: string = this.data.trackerName;
  loading = true;
  configs: TrackerConfig[] = [];
  displayedConfigs: TrackerConfig[] = [];
  searchText: string = '';

  ngOnInit() {
    this.trackerService.getConfigs().subscribe({
      next: (configs: TrackerConfig[]) => {
        this.configs = (configs || []).filter(c => !this.data.typeFilter || c.type === this.data.typeFilter);
        this.displayedConfigs = this.configs;
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  onSearchChange(value: string) {
    this.searchText = value;
    const search = value.trim().toLowerCase();
    this.displayedConfigs = !search
      ? this.configs
      : this.configs.filter(c => (c.name || '').toLowerCase().includes(search));
  }

  select(config: TrackerConfig) {
    this.dialogRef.close(config);
  }

  cancel() {
    this.dialogRef.close(undefined);
  }
}
